import React from 'react';
import { matchBand, MATCH_BAND_LABEL } from '../../../utils/diagramTag';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, ChevronDown, ChevronUp, Pencil } from 'lucide-react';
import { RefinePopup, RefineItem, AssumptionTooltip } from './MultiPartResultView';
import { isPartNumberQuery, splitPartAndRef } from '../../../utils/partLabel';
import { buildPartEditMarker } from '../../../utils/partEdits';
import { annotatedImageKey } from '../../../utils/annotatedImages';
import ImageZoomModal from '../../common/ImageZoomModal';

/** A part item parsed from the validation agent's response (or legacy HTML). */
export interface SummaryItem {
  /** The display name of the part (the new name for edited items). Kept in the
   *  search language (corrected_name + canonical qualifiers) — used for matching. */
  name: string;
  /** Backend-translated, client-language label SHOWN to the user (falls back to
   *  `name`). Display-only; never used for matching. */
  displayLabel?: string;
  /** Verbatim term entered by the user; used to seed edit/refine controls. */
  originalQuery?: string;
  /** The request line's 1-based number, assigned once for the life of the
   *  session and never recomputed — see ADR 0007. */
  n?: number;
  /**
   * normal     — regular new part being searched
   * removed    — user deleted it; crossed-out, skipped in search
   * warning    — needs attention (ambiguous side/position/color)
   * edited     — user renamed it; old name struck-through, new name active
   * not_in_oe  — universal consumable / commodity, not in OE catalog
   * in_cart    — user already added a part associated with this query to the cart
   */
  status: 'normal' | 'removed' | 'warning' | 'edited' | 'not_in_oe' | 'in_cart';
  /** Tooltip text shown for 'warning' items. */
  warning?: string;
  /** Original name before the edit, used for 'edited' items. */
  oldName?: string;
}

/** A found-part entry derived from message parts_results, used to mark rows live.
 *  `label`/`probability`/assumption fields mirror the MultiPartResultView card
 *  header exactly (built via buildResolvedPartHeader) — when a row is found, it
 *  is REPLACED by this result identity. */
export interface FoundPartRef {
  /** related_corrected_name reported by the search agent (matching key). */
  name: string;
  /** Resolved display label, identical to the result card header. */
  label: string;
  /** Warning-capped confidence 0–1, identical to the result card header. */
  probability?: number | null;
  /** True when the search made assumptions (shows the assumption icon). */
  hasWarnings?: boolean;
  resolutions?: string[] | null;
  resolutions_explanation?: string[] | null;
  resolution_keys?: string[] | null;
  /** All name variants usable for matching (corrected name, original query, label…). */
  matchNames?: string[];
  /** DOM id of the part result card, for scroll-to-part. */
  anchorId: string;
  /** How many candidate parts the search returned for this single request. >1
   *  means the user must pick one in the results — surfaced as an "N options"
   *  badge on the row so the tracker count (requested parts) lines up with the
   *  result list. */
  optionCount?: number;
  /** 0-based index of the original requested line in NEW_PARTS. */
  reqIndex?: number;
  /** Side/position of the resolved part ("Left", "Front"…) when the catalog
   *  carries it in extra_details rather than in the name. The results table shows
   *  it as a chip; the tracker row shows the SAME chip, so a left/right pair is
   *  told apart in both lists instead of reading as two identical rows. */
  positions?: string[];
}

/** Live search status overlaid on a SummaryItem.
 *  'no_result' = the search completed but produced no card for this row AND the
 *  agent never explicitly flagged it not_found — a silent gap we mark anyway so
 *  every requested part shows a clear status instead of a blank (no badge). */
type LiveStatus = 'searching' | 'found' | 'not_found' | 'no_result' | null;

interface TrackedPart {
  item: SummaryItem;
  /** Index in the original items array (stable for selection/refine). */
  idx: number;
  /**
   * Stable React key: the normalized-name dedupe key. Unlike `idx`, it does
   * not shift when the cumulative parts list reorders/merges between turns —
   * index keys made React reuse <li> subtrees across different parts, which
   * crashes reconciliation (insertBefore NotFoundError) when row content
   * changes shape mid-update.
   */
  key: string;
  live: LiveStatus;
  probability?: number | null;
  anchorId?: string;
  /** Search-result assumptions, rendered as the same icon as the result card. */
  assumptions?: Pick<FoundPartRef, 'resolutions' | 'resolutions_explanation' | 'resolution_keys'>;
  /** Candidate-part count for this request (>1 → "N options" badge). */
  optionCount?: number;
  /** 0-based position of the matched result in `foundParts` — equals the
   *  results-table row order, so the row's number lines up with the table. */
  resultIndex?: number;
  /** Side/position chips of the matched result (see FoundPartRef.positions). */
  positions?: string[];
}



/** The key a tracker row is deduped and rendered under.
 *
 *  A ledger line carries its own ordinal, and that is the key: two requested
 *  lines can share a name (both friso lines resolve to "Friso de Porta") and
 *  must still be two rows — merging them by name is how a ten-part request
 *  showed nine. Only a pre-ledger item, which has no ordinal, falls back to its
 *  normalized name, where the validation agent's occasional true duplicate is
 *  the thing being collapsed. Removed rows never merge with live ones. */
export function trackerRowKey(item: Pick<SummaryItem, 'name' | 'status' | 'n'>, idx: number): string {
  const prefix = item.status === 'removed' ? 'removed:' : '';
  if (typeof item.n === 'number' && item.n > 0) return `${prefix}line:${item.n}`;
  return `${prefix}${normPartName(item.name) || `__${idx}`}`;
}

/** Build the normalized cart-name list used for in_cart matching.
 *  Extracted from BotMessage so the panel and bubbles share one implementation. */
export function buildCartNameList(cartItems?: any[]): string[] {
  return (cartItems ?? []).flatMap((ci: any) => {
    const pd = ci.structured_data?.part_data || ci.structured_data?.PART_DATA || {};
    return [
      ci.query,
      ci.related_user_query,
      pd.query,
      pd.related_user_query,
      pd.description,
      pd.DESCRIPTION,
      pd.corrected_name,
      pd.original_name,
      pd.part_name,
    ].filter(Boolean).map((s: string) => s.trim().toLowerCase());
  });
}

// ── Fuzzy part-name matching ─────────────────────────────────────────────────
// Display names, search-result names and cart queries differ in punctuation,
// accents and abbreviations ("Guía, Delantero, Derecho" vs "Guia delant der"),
// so plain substring checks miss most links. Tokens are accent-folded,
// punctuation-stripped, stopword-free, and matched prefix-tolerantly so
// abbreviated words ("delant" → "delantero") still hit.

const NAME_STOPWORDS = new Set([
  'de', 'del', 'la', 'el', 'los', 'las', 'un', 'una', 'y', 'o', // es
  'the', 'of', 'an', 'and', 'or',                               // en
  'da', 'do', 'das', 'dos', 'e', 'em', 'no', 'na',              // pt
]);

function nameTokens(name: string): string[] {
  return (name || '')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter(w => w.length >= 2 && !NAME_STOPWORDS.has(w));
}

/** Two tokens match when equal, or one is a ≥3-char prefix of the other. */
function tokensMatch(a: string, b: string): boolean {
  if (a === b) return true;
  const [short, long] = a.length <= b.length ? [a, b] : [b, a];
  return short.length >= 3 && long.startsWith(short);
}

/** True when every token of the shorter side matches a token of the longer side. */
function tokenSetsMatch(a: string[], b: string[]): boolean {
  if (a.length === 0 || b.length === 0) return false;
  const [short, long] = a.length <= b.length ? [a, b] : [b, a];
  return short.every(s => long.some(l => tokensMatch(s, l)));
}

/** True when the sides cover each other — EVERY token on BOTH sides matches one
 *  on the other. The one-sided `tokenSetsMatch` above is a subset test, so a
 *  generic request is "matched" by any more specific part that merely contains
 *  its words: "Para-choques trás" ⊂ "Forra inferior para-choques trás" made the
 *  bumper row read "in cart" as soon as the LOWER COVER was added (live, guest
 *  chat 2026-08-26). The extra tokens are exactly what distinguishes the parts
 *  ("inferior", "difusor", "superior"), so they cannot be ignored for identity.
 *  Prefix tolerance is kept, so abbreviations ("Guia delant der" vs "Guía,
 *  Delantero, Derecho") still match both ways. */
function tokenSetsCoverEachOther(a: string[], b: string[]): boolean {
  if (a.length === 0 || b.length === 0) return false;
  return a.every(x => b.some(y => tokensMatch(x, y)))
      && b.every(y => a.some(x => tokensMatch(x, y)));
}

/** Normalize a part name for identity comparison — strip accents, lowercase,
 *  collapse non-alphanumerics to single spaces. Shared with MultiPartResultView
 *  so both number requested parts off the SAME distinct-name set. */
export function normPartName(name: string): string {
  return (name || '')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

/** Match a summary-item display name against another part name. The item's main
 *  name and its "(original wording)" suffix are tried separately. */
export function partNamesMatch(itemName: string, otherName: string): boolean {
  const other = nameTokens(otherName);
  if (other.length === 0) return false;
  const main = nameTokens(itemName.replace(/\(.*?\)/g, ' '));
  if (main.length > 0 && tokenSetsMatch(main, other)) return true;
  const paren = itemName.match(/\(([^)]+)\)/);
  if (paren) {
    const orig = nameTokens(paren[1]);
    if (orig.length > 0 && tokenSetsMatch(orig, other)) return true;
  }
  return false;
}

/** Identity-grade name match: like {@link partNamesMatch}, but both sides must
 *  cover each other, so a strict token-subset is NOT a match. Used for cart
 *  membership, where a false positive tells the user a part is already bought.
 *  Ranking/pairing keeps the looser `partNamesMatch` — there, a subset is a
 *  legitimate weak signal that the scoring tiers then rank. */
export function partNamesMatchStrict(itemName: string, otherName: string): boolean {
  const other = nameTokens(otherName);
  if (other.length === 0) return false;
  const main = nameTokens(itemName.replace(/\(.*?\)/g, ' '));
  if (main.length > 0 && tokenSetsCoverEachOther(main, other)) return true;
  const paren = itemName.match(/\(([^)]+)\)/);
  if (paren) {
    const orig = nameTokens(paren[1]);
    if (orig.length > 0 && tokenSetsCoverEachOther(orig, other)) return true;
  }
  return false;
}



/** Cart matching: exact/substring fast path, then token-based fuzzy match. */
export function isInCartByName(name: string, cartNameList: string[]): boolean {
  if (cartNameList.length === 0) return false;
  const nameLower = name.trim().toLowerCase();
  if (cartNameList.includes(nameLower)) return true;
  return cartNameList.some(
    cartStr =>
      // The item name may ADD qualifiers around the cart string ("Forra inferior
      // para-choques trás (Forra Inferior do Para-choques, Traseiro)" contains the
      // cart query) — that still identifies the same part. The reverse direction
      // (`cartStr.includes(nameLower)`) is NOT safe: it makes a generic row match
      // any longer, more specific cart line, which is how the rear-bumper row read
      // "in cart" after only the lower cover was added.
      (cartStr.length > 2 && nameLower.includes(cartStr)) ||
      partNamesMatchStrict(name, cartStr)
  );
}

/** One part row. Shared by the floating tracker panel and (legacy fallback) the
 *  in-bubble validation card in BotMessage. */
export const SummaryPartItem: React.FC<{
  item: SummaryItem;
  t: (key: string, opts?: any) => string;
  isSelected?: boolean;
  onToggleSelect?: () => void;
  showCheckbox?: boolean;
  /** Live search status badge (tracker panel only). */
  live?: LiveStatus;
  probability?: number | null;
  onJumpToPart?: () => void;
  /** Search-result assumptions — renders the same info icon as the result card. */
  assumptions?: Pick<FoundPartRef, 'resolutions' | 'resolutions_explanation' | 'resolution_keys'>;
  /** When >1, the search returned several candidates for this one request. */
  optionCount?: number;
  /** 1-based tracking number shown at the start of the row so the user can
   *  reference each item (mirrors the "#" column in the results table). */
  index?: number;
  /** Side/position of the resolved part, shown as the same chip the results
   *  table renders under the OE description. */
  positions?: string[];
  /** The user's photo this part was identified in (annotated when marked) —
   *  shown as a thumbnail on the row; click opens it. */
  photoUrl?: string | null;
  onPhotoClick?: (url: string) => void;
}> = ({ item, t, isSelected = false, onToggleSelect, showCheckbox = false, live = null, probability, onJumpToPart, assumptions, optionCount, index, positions, photoUrl, onPhotoClick }) => {
  const [showTooltip, setShowTooltip] = React.useState(false);
  // The "Requires clarification" tooltip is rendered in a PORTAL: the tracker's
  // scrollable <ul> has overflow:auto, which clips an absolutely-positioned tooltip
  // inside a row. A fixed-position portal escapes that clip so the tip is visible.
  const clarifRef = React.useRef<HTMLSpanElement>(null);
  const [tipPos, setTipPos] = React.useState<{ top: number; left: number } | null>(null);
  const openTip = () => {
    const r = clarifRef.current?.getBoundingClientRect();
    if (r) setTipPos({ top: r.top, left: r.right });
    setShowTooltip(true);
  };
  const closeTip = () => setShowTooltip(false);
  // Show the backend's client-language label; item.name (search language) is the
  // fallback and remains the matching key everywhere else. The label is composed
  // "Resolved Name (original)" — our reading of the request leads, the
  // customer's own words follow in parentheses ("Bumper, Rear (bumper)").
  const composedLabel = item.displayLabel ?? item.name;
  const shown = composedLabel;

  // Same assumption icon + modal as the result card header. Wrapped to keep the
  // click from toggling row selection.
  const assumptionIcon = assumptions ? (
    <span onClick={e => e.stopPropagation()} style={{ display: 'inline-flex', flexShrink: 0 }}>
      <AssumptionTooltip
        lines={assumptions.resolutions ?? []}
        resolutions_explanation={assumptions.resolutions_explanation}
        resolution_keys={assumptions.resolution_keys}
      />
    </span>
  ) : null;

  // Side/position chips ("Left", "Front") — the qualifier that tells same-named
  // parts apart when the catalog carries it in extra_details instead of in the
  // name. Same palette as the results-table chip (MultiPartResultView
  // renderPositionChips) so one part reads identically in both lists.
  const positionBadges = (positions ?? []).length > 0 ? (
    <>
      {positions!.map((p, i) => (
        <span
          key={`pos-${i}`}
          style={{
            flexShrink: 0, fontSize: '10px', fontWeight: 600, color: '#3627BF',
            backgroundColor: '#eef0ff', border: '1px solid #d6d8ff',
            borderRadius: '6px', padding: '0 6px', whiteSpace: 'nowrap',
          }}
        >
          {p}
        </span>
      ))}
    </>
  ) : null;

  // "N options" badge — the search returned several candidate parts for this one
  // request, so the row stays a single requested part (keeping the tracker count
  // aligned with the result header) while signalling that a choice is pending.
  const optionsBadge = optionCount && optionCount > 1 ? (
    <span
      title={t('chat.partsTracker.optionsHint', { defaultValue: '{{n}} matching parts were found — pick one in the results', n: optionCount })}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: '3px',
        fontSize: '10px', fontWeight: 600, color: '#92400e', backgroundColor: '#fef3c7',
        border: '1px solid #f59e0b', borderRadius: '4px',
        padding: '1px 5px', whiteSpace: 'nowrap', flexShrink: 0,
      }}>
      {t('chat.partsTracker.options', { defaultValue: '{{n}} options', n: optionCount })}
    </span>
  ) : null;

  const liStyle: React.CSSProperties = {
    display: 'flex', alignItems: 'center', gap: '6px',
    fontSize: '13px', color: '#4b4945', lineHeight: '18px',
    padding: '7px 8px', borderRadius: '9px',
    backgroundColor: isSelected ? '#efedff' : 'transparent',
    transition: 'background-color 180ms ease, transform 180ms ease',
    borderBottom: '1px solid #f1efeb',
    borderLeft: '2px solid #8b82ff'
  };

  const bullet = showCheckbox ? (
    <input
      type="checkbox"
      checked={isSelected}
      onChange={onToggleSelect}
      onClick={e => e.stopPropagation()}
      style={{ flexShrink: 0, marginTop: '1px', accentColor: '#6866ff', cursor: 'pointer', width: '13px', height: '13px' }}
    />
  ) : (
    <span style={{ flexShrink: 0, width: '5px', height: '5px', borderRadius: '50%', backgroundColor: '#6866ff', display: 'inline-block' }} />
  );

  // 1-based tracking number badge, rendered at the very start of every row so it
  // lines up with the "#" column in the results table.
  const numberTag = index != null ? (
    <span style={{ flexShrink: 0, minWidth: '16px', height: '16px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '9px', fontWeight: 700, color: '#8a857c', backgroundColor: '#f0ede9', borderRadius: '4px', padding: '0 3px' }}>{index}</span>
  ) : null;
  // The part's own photo rides next to its number, so every status branch below
  // shows it without each one having to place it.
  const numberBadge = photoUrl ? (
    <>
      {numberTag}
      <img
        src={photoUrl}
        alt={t('chat.partsTracker.partPhoto', { defaultValue: 'Your photo of this part' })}
        onClick={onPhotoClick ? (e) => { e.stopPropagation(); onPhotoClick(photoUrl); } : undefined}
        style={{
          flexShrink: 0, height: '32px', width: '32px', borderRadius: '5px',
          border: '1px solid #e5e7eb', objectFit: 'cover',
          cursor: onPhotoClick ? 'zoom-in' : 'default',
        }}
      />
    </>
  ) : numberTag;

  // Right-side live badge shown for rows still in the search lifecycle.
  const liveBadge = (() => {
    if (live === 'searching') {
      return (
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: '4px',
          fontSize: '10px', fontWeight: 600, color: '#6866ff', whiteSpace: 'nowrap',
        }}>
          <Loader2 size={10} className="animate-spin" />
          {t('chat.partsTracker.searching', { defaultValue: 'Searching…' })}
        </span>
      );
    }
    if (live === 'found') {
      const pct = probability != null ? Math.round(Number(probability) * 100) : null;
      const hue = pct != null ? Math.round((pct / 100) * 120) : 120;
      return (
        <span
          onClick={onJumpToPart ? (e) => { e.stopPropagation(); onJumpToPart(); } : undefined}
          title={onJumpToPart ? t('chat.partsTracker.jumpToPart', { defaultValue: 'Show result' }) : undefined}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '4px',
            fontSize: '10px', fontWeight: 700, whiteSpace: 'nowrap',
            color: `hsl(${hue}, 70%, 30%)`, backgroundColor: `hsl(${hue}, 80%, 93%)`,
            borderRadius: '999px', padding: '2px 8px',
            cursor: onJumpToPart ? 'pointer' : 'default',
          }}>
          ✓ {pct != null ? (() => { const b = matchBand(Number(probability)) || 'low'; return t(MATCH_BAND_LABEL[b].key, { defaultValue: MATCH_BAND_LABEL[b].defaultValue }); })() : t('chat.partsTracker.found', { defaultValue: 'Found' })}
          {onJumpToPart && <span aria-hidden="true">↗</span>}
        </span>
      );
    }
    if (live === 'not_found') {
      return (
        <span style={{
          display: 'inline-flex', alignItems: 'center',
          fontSize: '10px', fontWeight: 600, color: '#b91c1c', backgroundColor: '#fee2e2',
          borderRadius: '20px', padding: '1px 7px', whiteSpace: 'nowrap',
        }}>
          {t('chat.partsTracker.notFound', { defaultValue: 'Not found' })}
        </span>
      );
    }
    if (live === 'no_result') {
      // Amber (not red): the catalog didn't confirm absence — the search just
      // returned nothing for this row, so the user needs to clarify/rephrase.
      // Framed as an actionable "Requires clarification" CTA (with an alert icon)
      // rather than a dead-end "No result".
      return (
        <span
          title={t('chat.partsTracker.noResultHint', { defaultValue: 'No catalog result was returned for this part — try rephrasing or searching it again.' })}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '3px',
            fontSize: '10px', fontWeight: 600, color: '#92400e', backgroundColor: '#FEF3C7',
            border: '1px solid #f59e0b', borderRadius: '4px', padding: '1px 6px', whiteSpace: 'nowrap',
          }}>
          ⚠ {t('chat.partsTracker.requiresClarification', { defaultValue: 'Requires clarification' })}
        </span>
      );
    }
    return null;
  })();

  // ── removed ──────────────────────────────────────────────────────────────
  if (item.status === 'removed') {
    return (
      <li style={{ ...liStyle, color: '#aaa', textDecoration: 'line-through', borderLeft: '3px solid #f59e0b' }} onClick={onToggleSelect} className={onToggleSelect ? 'cursor-pointer' : ''}>
        {numberBadge}
        <span style={{ flexShrink: 0, fontSize: '12px' }}>✕</span>
        <span style={{ flex: 1 }}>{shown}</span>
      </li>
    );
  }

  // ── edited ────────────────────────────────────────────────────────────────
  if (item.status === 'edited') {
    return (
      <li style={{ ...liStyle, borderLeft: '3px solid #f59e0b' }} onClick={onToggleSelect} className={onToggleSelect ? 'cursor-pointer' : ''}>
        {numberBadge}
        {bullet}
        <span style={{ flex: 1 }}>
          {item.oldName && (
            <span style={{ textDecoration: 'line-through', color: '#aaa', marginRight: '4px' }}>{item.oldName}</span>
          )}
          <span style={{ fontWeight: 500 }}>{shown}</span>
        </span>
        {positionBadges}
        {optionsBadge}
        {liveBadge ?? (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: '3px',
            fontSize: '10px', color: '#6866ff', backgroundColor: '#ededff',
            border: '1px solid #c7c6ff', borderRadius: '4px',
            padding: '1px 5px', whiteSpace: 'nowrap',
          }}>
            ✎ {t('chat.botMessage.edited', { defaultValue: 'Edited' })}
          </span>
        )}
        {assumptionIcon}
      </li>
    );
  }

  // ── not_in_oe ─────────────────────────────────────────────────────────────
  // The status is an LLM classification made BEFORE any search — a later
  // search can find the part anyway (observed live: "tires" → catalog TIRE).
  // A live found/searching state wins: fall through to the normal row so the
  // ✓ badge renders instead of a stale "Not in catalog".
  if (item.status === 'not_in_oe' && live !== 'found' && live !== 'searching') {
    return (
      <li style={{ ...liStyle, color: '#888', borderLeft: '3px solid #f59e0b' }} onClick={onToggleSelect} className={onToggleSelect ? 'cursor-pointer' : ''}>
        {numberBadge}
        {bullet}
        <span style={{ flex: 1 }}>{shown}</span>
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: '3px',
          fontSize: '10px', fontWeight: 600, color: '#92400e',
          backgroundColor: '#fef3c7',
          border: '1px solid #f59e0b', borderRadius: '4px',
          padding: '1px 5px', whiteSpace: 'nowrap',
        }}>
          {t('chat.botMessage.notInOe', { defaultValue: 'Not in catalog' })}
        </span>
      </li>
    );
  }

  // ── warning ───────────────────────────────────────────────────────────────
  if (item.status === 'warning') {
    return (
      <li style={{ ...liStyle, borderLeft: '3px solid #f59e0b', backgroundColor: isSelected ? '#efedff' : '#fffbeb' }} onClick={onToggleSelect} className={onToggleSelect ? 'cursor-pointer' : ''}>
        {numberBadge}
        {bullet}
        <span style={{ flex: 1 }}>{shown}</span>
        {positionBadges}
        {optionsBadge}
        {/* Suppress the no_result "Requires clarification" badge here — the warning
            chip below already says the same thing, else it renders twice. */}
        {live !== 'no_result' && liveBadge}
        <span
          ref={clarifRef}
          style={{ position: 'relative', flexShrink: 0 }}
          onMouseEnter={item.warning ? openTip : undefined}
          onMouseLeave={closeTip}
          onClick={e => { e.stopPropagation(); if (!item.warning) return; showTooltip ? closeTip() : openTip(); }}
        >
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: '3px',
            fontSize: '10px', fontWeight: 600, color: '#92400e', backgroundColor: '#fef3c7',
            border: '1px solid #f59e0b', borderRadius: '4px',
            padding: '1px 6px', cursor: item.warning ? 'help' : 'default', whiteSpace: 'nowrap',
          }}>
            ⚠ {t('chat.partsTracker.requiresClarification', { defaultValue: 'Requires clarification' })}
          </span>
          {showTooltip && item.warning && tipPos && createPortal(
            <span style={{
              position: 'fixed', top: tipPos.top, left: tipPos.left,
              transform: 'translate(-100%, calc(-100% - 6px))',
              backgroundColor: '#1f2937', color: '#f9fafb',
              fontSize: '11px', lineHeight: '15px', padding: '6px 9px',
              borderRadius: '6px', whiteSpace: 'normal', maxWidth: '240px',
              zIndex: 2147483647, boxShadow: '0 2px 10px rgba(0,0,0,0.25)',
              pointerEvents: 'none',
            }}>
              {item.warning}
            </span>,
            document.body,
          )}
        </span>
      </li>
    );
  }

  // ── in_cart ───────────────────────────────────────────────────────────────
  if (item.status === 'in_cart') {
    return (
      <li style={{ ...liStyle, color: '#137f67', borderLeft: '3px solid #6edbb8' }} onClick={onToggleSelect} className={onToggleSelect ? 'cursor-pointer' : ''}>
        {numberBadge}
        {bullet}
        <span style={{ flex: 1 }}>{shown}</span>
        {positionBadges}
        {optionsBadge}
        {liveBadge}
        {assumptionIcon}
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: '3px',
          padding: '1px 5px', whiteSpace: 'nowrap',
        }}>
          ✓ {t('chat.botMessage.inCart', { defaultValue: 'In cart' })}
        </span>
      </li>
    );
  }

  // ── normal ────────────────────────────────────────────────────────────────
  return (
    <li style={liStyle} onClick={onToggleSelect} className={onToggleSelect ? 'cursor-pointer' : ''}>
      {numberBadge}
      {bullet}
      <span style={{ flex: 1 }}>{shown}</span>
      {positionBadges}
      {optionsBadge}
      {liveBadge}
      {assumptionIcon}
    </li>
  );
};

interface PartsTrackerPanelProps {
  /** Cumulative parts list from the LATEST validation response. */
  items: SummaryItem[];
  serviceTitle?: string;
  /** Flattened search results across all messages (latest occurrence per name wins). */
  foundParts: FoundPartRef[];
  /** Names the search agent reported as not found. */
  notFoundParts: string[];
  cartItems?: any[];
  /** True while a turn is streaming — unresolved rows show "Searching…". */
  isSearching: boolean;
  readOnly?: boolean;
  /** Sends a hidden chat message (used by the Refine flow). */
  onSendSilent?: (text: string) => void;
  /** Annotated analysis images collected across the session. */
  annotatedImages?: string[];
  /** {part, image_url} per vision-identified part — shown on its own row. */
  visionPartImages?: Array<{ part: string; image_url: string }>;
}

/** Floating, sticky parts tracker. Single live rendering of the session's parts
 *  list — replaces the per-bubble validation cards. Collapsed: a slim counter
 *  strip. Expanded: an overlay dropdown (mobile gets a dimmed backdrop). */
const PartsTrackerPanel: React.FC<PartsTrackerPanelProps> = ({
  items,
  serviceTitle,
  foundParts,
  notFoundParts,
  cartItems,
  isSearching,
  readOnly = false,
  onSendSilent,
  annotatedImages,
  visionPartImages,
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = React.useState(false);
  const [lightboxUrl, setLightboxUrl] = React.useState<string | null>(null);
  const [pulse, setPulse] = React.useState(false);
  const [refineItems, setRefineItems] = React.useState<RefineItem[] | null>(null);
  const hadItemsRef = React.useRef(false);
  const panelRef = React.useRef<HTMLDivElement>(null);

  // Close on outside click via a document listener instead of a full-screen
  // backdrop. A backdrop overlay (fixed inset-0) swallowed wheel/touch events,
  // so the chat page couldn't be scrolled while the list was expanded. With a
  // plain listener the page scrolls normally and an outside click still closes.
  React.useEffect(() => {
    if (!expanded) return;
    const handlePointerDown = (e: MouseEvent | TouchEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setExpanded(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
    };
  }, [expanded]);

  // Derive live status per row — the single place statuses are computed.
  // Each row's own photo: the vision part name IS the request line that seeded
  // it, matched the same way as the results table (visionImageFor).
  const photoFor = React.useCallback((item: SummaryItem): string | null => {
    if (!visionPartImages?.length) return null;
    for (const term of [item.originalQuery, item.name]) {
      const s = (term || '').trim();
      if (!s) continue;
      const hit = visionPartImages.find(vp => vp?.part && vp.image_url && partNamesMatch(s, vp.part));
      if (hit) return hit.image_url;
    }
    return null;
  }, [visionPartImages]);

  const tracked: TrackedPart[] = React.useMemo(() => {
    const cartNameList = buildCartNameList(cartItems);
    // A search has actually produced results this turn (cards or not-found flags)
    // — only then is it meaningful to mark a result-less row as 'no_result'.
    const searchHasRun = (foundParts?.length ?? 0) > 0 || (notFoundParts?.length ?? 0) > 0;

    // Result → row is a lookup, not a match: a found reference carries the
    // position of the line it answers, which is this row's own position.
    const assignableStatus = (s: string) => s === 'normal' || s === 'warning' || s === 'edited' || s === 'in_cart';
    const assignedFp = new Map<number, number>();
    foundParts.forEach((fp, fpIdx) => {
      const idx = fp.reqIndex;
      if (typeof idx !== 'number' || !items[idx]) return;
      if (!assignableStatus(items[idx].status)) return;
      assignedFp.set(idx, fpIdx);
    });

    const rows = items.map((raw, idx) => {
      let item: SummaryItem = raw;

      let live: LiveStatus = null;
      let probability: number | null | undefined;
      let anchorId: string | undefined;
      let assumptions: TrackedPart['assumptions'];
      let optionCount: number | undefined;
      let resultIndex: number | undefined;
      let positions: string[] | undefined;
      if (raw.status === 'normal' || raw.status === 'warning' || raw.status === 'edited' || raw.status === 'in_cart') {
        // Use the injective assignment computed above (no two rows share a result).
        const fpIdx = assignedFp.get(idx);
        const found: FoundPartRef | undefined = fpIdx != null ? foundParts[fpIdx] : undefined;
        if (found) {
          // The result REPLACES the requested part: same label, probability and
          // assumption icon as the MultiPartResultView card header. The search
          // result supersedes the validation warning, so 'warning' is demoted.
          live = 'found';
          probability = found.probability;
          anchorId = found.anchorId;
          optionCount = found.optionCount;
          resultIndex = fpIdx;
          positions = found.positions;
          if (found.hasWarnings) {
            assumptions = {
              resolutions: found.resolutions,
              resolutions_explanation: found.resolutions_explanation,
              resolution_keys: found.resolution_keys,
            };
          }
          item = {
            ...raw,
            name: raw.name || found.label,
            // A part-number request ("SKU 10588708") renders as the resolved
            // catalog name once found — found.label already reads "Name (SKU …)"
            // (buildResolvedPartHeader). Matching still keys on `name`.
            displayLabel: isPartNumberQuery(raw.name) && found.label && !isPartNumberQuery(found.label)
              ? found.label
              : raw.displayLabel,
            status: raw.status === 'warning' ? 'normal' : raw.status,
          };
        } else if (notFoundParts.some(nf =>
          partNamesMatch(raw.name, nf) ||
          // not_found terms now arrive as the user's ORIGINAL wording, which can
          // share no tokens with the canonical name — the displayLabel carries
          // that original as its parenthetical, so match through it too.
          (raw.displayLabel ? partNamesMatch(raw.displayLabel, nf) : false))) {
          live = 'not_found';
        } else if (isSearching && raw.status !== 'in_cart') {
          live = 'searching';
        } else if (!isSearching && searchHasRun && raw.status !== 'in_cart') {
          // Search finished and produced results for OTHER rows, but nothing for
          // this one and the agent never flagged it not_found → mark it explicitly
          // so a requested part is never shown blank/linkless.
          live = 'no_result';
        }
      }
      // Cart matching considers both the requested and the resolved name.
      const inCart = item.status !== 'removed' && item.status !== 'not_in_oe' &&
        item.status !== 'in_cart' &&
        (isInCartByName(item.name, cartNameList) || isInCartByName(raw.name, cartNameList));
      if (inCart) item = { ...item, status: 'in_cart' };
      // In-cart rows keep the found probability badge + assumption icon, but a
      // stale "searching" spinner makes no sense once the part is in the cart.
      if (item.status === 'in_cart' && live === 'searching') live = null;
      return { item, idx, key: '', live, probability, anchorId, assumptions, optionCount, resultIndex, positions };
    });

    // Dedupe rows that resolve to the same display name — the validation agent
    // can emit the same part twice (e.g. two identical "Door Window Weatherstrip,
    // Rear, Right" rows). Keep a single row, preferring the most-resolved state
    // (in-cart > found > searching > not-found) and carrying over its anchor,
    // probability and assumptions so nothing is lost in the merge.
    const resolveScore = (p: TrackedPart) =>
      p.item.status === 'in_cart' ? 4
        : p.live === 'found' ? 3
        : p.live === 'searching' ? 2
        : p.live === 'not_found' ? 1
        : p.live === 'no_result' ? 1
        : 0;
    const byKey = new Map<string, TrackedPart>();
    const order: string[] = [];
    for (const row of rows) {
      const key = trackerRowKey(row.item, row.idx);
      const existing = byKey.get(key);
      if (!existing) { byKey.set(key, { ...row, key }); order.push(key); continue; }
      const [base, other] = resolveScore(row) > resolveScore(existing) ? [row, existing] : [existing, row];
      byKey.set(key, {
        ...base,
        key,
        anchorId: base.anchorId ?? other.anchorId,
        probability: base.probability ?? other.probability,
        assumptions: base.assumptions ?? other.assumptions,
        optionCount: base.optionCount ?? other.optionCount,
        resultIndex: base.resultIndex ?? other.resultIndex,
        positions: base.positions?.length ? base.positions : other.positions,
      });
    }
    // Keep the tracker in the STABLE requested-parts (validation) order —
    // first-seen order never shifts between iterations (searching → found →
    // clarification), so the list doesn't jump around as results arrive. The
    // 1→N numbering below is what ties it to the results table, not the order.
    return order.map(k => byKey.get(k)!);
  }, [items, foundParts, notFoundParts, cartItems, isSearching]);

  // Row numbers come from the request line itself (ADR 0007), so the tracker and
  // the results table cannot number the same part differently.


  const counters = React.useMemo(() => ({
    total: tracked.filter(p => p.item.status !== 'removed').length,
    found: tracked.filter(p => p.live === 'found' || p.item.status === 'in_cart').length,
    searching: tracked.filter(p => p.live === 'searching').length,
    warnings: tracked.filter(p => p.item.status === 'warning').length,
  }), [tracked]);

  // The list never opens on its own — the first validation of the session only
  // arms the pulse, same as every later status change, so the overlay never
  // covers the chat unless the user opened it.
  React.useEffect(() => {
    if (items.length > 0 && !hadItemsRef.current) {
      hadItemsRef.current = true;
    }
    if (items.length === 0) {
      hadItemsRef.current = false;
      setExpanded(false);
    }
  }, [items.length]);

  const statusSignature = tracked.map(p => `${p.item.status}:${p.live}`).join('|');
  React.useEffect(() => {
    if (!hadItemsRef.current) return;
    setPulse(true);
    const timer = setTimeout(() => setPulse(false), 1200);
    return () => clearTimeout(timer);
  }, [statusSignature]);

  if (items.length === 0) return null;

  const openRefine = () => {
    const active = tracked.filter(p => p.item.status !== 'removed');
    setRefineItems(active.map(p => {
      const { partName, ref } = splitPartAndRef(p.item.displayLabel || p.item.name);
      const term = p.item.originalQuery || ref || p.item.name;
      // The row edits as it reads ("Bumper, Rear"), and that label IS the
      // edit's identity: one raw term can stand behind several lines (see
      // MultiPartResultView.editResult). A number request keeps its number.
      const label = partName || term;
      const identity = isPartNumberQuery(term) ? term : label;
      return { original: identity, value: label, label };
    }));
  };

  const jumpTo = (anchorId: string) => {
    const el = document.getElementById(anchorId);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      if (el.tagName === 'TR') {
        // Table-view anchor: a box-shadow ring gets clipped on table rows, so
        // flash the row background instead.
        el.style.transition = 'background-color 0.3s ease';
        el.style.backgroundColor = 'rgba(104, 102, 255, 0.16)';
        setTimeout(() => { el.style.backgroundColor = ''; }, 1600);
      } else {
        // Flash a highlight ring so the eye lands on the right card.
        el.style.transition = 'box-shadow 0.3s ease';
        el.style.boxShadow = '0 0 0 3px rgba(104, 102, 255, 0.55)';
        setTimeout(() => { el.style.boxShadow = ''; }, 1600);
      }
    }
    setExpanded(false);
  };

  // Images a row already shows are not repeated in the shared strip; only the
  // ones no row claims (re-signed URLs compare by path) stay there.
  const claimedImageKeys = new Set(
    tracked.map(p => photoFor(p.item)).filter((u): u is string => !!u).map(annotatedImageKey),
  );
  const unclaimedImages = (annotatedImages ?? []).filter(u => !claimedImageKeys.has(annotatedImageKey(u)));

  return (
    <div className="sticky top-0 z-30 px-3 sm:px-6 pt-2" data-testid="parts-tracker-panel">
      {/* Refine popup — fixed-position modal, reused from MultiPartResultView */}
      {refineItems !== null && (
        <RefinePopup
          items={refineItems}
          t={t}
          onSubmit={(refined) => {
            const marker = buildPartEditMarker(refined);
            if (!marker.endsWith('%5B%5D')) onSendSilent?.(marker);
          }}
          onClose={() => setRefineItems(null)}
        />
      )}

      {/* Floating pill, centered over the chat column (same width as messages) */}
      <div ref={panelRef} className="relative max-w-4xl mx-auto">
        {/* Collapsed strip */}
        <div
          role="button"
          tabIndex={0}
          onClick={() => setExpanded(prev => !prev)}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpanded(prev => !prev); } }}
          className={`w-full flex items-center gap-2 px-3 sm:px-4 ${pulse ? 'animate-pulse' : ''}`}
          style={{
            height: '40px',
            backgroundColor: '#ffffff',
            border: '1px solid #e5e3df',
            borderRadius: expanded ? '16px 16px 0 0' : '16px',
            boxShadow: expanded ? '0 8px 24px rgba(42,36,25,0.10)' : '0 3px 12px rgba(42,36,25,0.07)',
            transition: 'border-radius 180ms ease, box-shadow 180ms ease, background-color 180ms ease',
            cursor: 'pointer',
            textAlign: 'left',
          }}
          aria-expanded={expanded}
        >
          {expanded
            ? <ChevronUp size={13} style={{ color: '#999', flexShrink: 0 }} />
            : <ChevronDown size={13} style={{ color: '#999', flexShrink: 0 }} />}
          <span className="hidden sm:inline" style={{ fontSize: '11px', fontWeight: 600, color: '#999', letterSpacing: '0.04em', textTransform: 'uppercase', flexShrink: 0 }}>
            {t('chat.botMessage.partsSummary', { defaultValue: 'Parts to Search' })}
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: '11px', fontWeight: 600, minWidth: 0 }}>
            {counters.found > 0 && (
              <span style={{ color: '#137f67', whiteSpace: 'nowrap' }}>✓ {counters.found}/{counters.total}</span>
            )}
            {counters.found === 0 && (
              <span style={{ color: '#777', whiteSpace: 'nowrap' }}>{counters.total}</span>
            )}
            {counters.searching > 0 && (
              <span style={{ color: '#6866ff', display: 'inline-flex', alignItems: 'center', gap: '3px', whiteSpace: 'nowrap' }}>
                <Loader2 size={10} className="animate-spin" /> {counters.searching}
              </span>
            )}
            {counters.warnings > 0 && (
              <span style={{ color: '#92400e', whiteSpace: 'nowrap' }}>⚠ {counters.warnings}</span>
            )}
          </span>
          {!readOnly && !isSearching && onSendSilent && (
            <button
              onClick={e => { e.stopPropagation(); openRefine(); }}
              style={{
                flexShrink: 0,
                display: 'inline-flex', alignItems: 'center', gap: '3px',
                padding: '3px 9px', fontSize: '11px', fontWeight: 650, marginLeft: '4px',
                color: '#6866ff', backgroundColor: 'transparent',
                border: '1px solid #d7d4ff', borderRadius: '999px',
                cursor: 'pointer', lineHeight: '14px', whiteSpace: 'nowrap',
                transition: 'background-color 160ms ease, border-color 160ms ease, transform 160ms ease',
              }}
              onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#efedff')}
              onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
              title={t('chat.partEdit.action', { defaultValue: 'Edit parts' })}
            >
              <Pencil size={11} />
              {t('chat.partEdit.action', { defaultValue: 'Edit parts' })}
            </button>
          )}
          <span style={{ flex: 1 }} />
          {serviceTitle && (
            <span className="hidden md:inline" style={{
              fontSize: '10px', color: '#6866ff', backgroundColor: '#ededff',
              border: '1px solid #c7c6ff', borderRadius: '20px',
              padding: '1px 8px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '180px',
            }}>
              {t(`orders.serviceTypes.${serviceTitle}`, { defaultValue: serviceTitle })}
            </span>
          )}
        </div>

        {/* Expanded overlay — no full-screen backdrop (it blocked page scroll);
            outside-click closing is handled by the document listener above. */}
        {expanded && (
          <>
            <div
              className="absolute left-0 right-0 z-30"
              style={{
                backgroundColor: '#ffffff',
                border: '1px solid #e5e3df',
                borderTop: 'none',
                borderRadius: '0 0 17px 17px',
                boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                // Constrain the whole dropdown to the viewport and let the list
                // scroll inside it, so long lists + annotated images stay reachable.
                // Subtract the panel's top offset (~90px) AND the fixed chat-input
                // bar at the bottom (~210px) so a long list stops above the input
                // and scrolls internally instead of spilling behind it. Floor keeps
                // it usable on very short viewports.
                display: 'flex',
                flexDirection: 'column',
                maxHeight: 'max(220px, calc(100vh - 300px))',
                overflow: 'hidden',
              }}
            >
              {/* Annotated analysis images from the session */}
              {/* Items — row click focuses the part's result card in the chat */}
              <ul
                className="overflow-y-auto notranslate"
                // translate="no": browser auto-translate (Google Translate) rewrites
                // text nodes inside these rows with <font> wrappers; React then fails
                // reconciliation with "insertBefore … not a child of this node".
                // Row content is already localized server-side via client_language.
                translate="no"
                style={{ margin: 0, padding: '10px 12px 12px', listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '3px', flex: '1 1 auto', minHeight: 0 }}
              >
                {tracked.map(p => (
                  <SummaryPartItem
                    key={p.key}
                    photoUrl={photoFor(p.item)}
                    onPhotoClick={setLightboxUrl}
                    index={p.item.status === 'removed' ? undefined : p.item.n}
                    item={p.item}
                    t={t}
                    live={p.live}
                    probability={p.probability}
                    assumptions={p.assumptions}
                    optionCount={p.optionCount}
                    positions={p.positions}
                    onJumpToPart={p.anchorId ? () => jumpTo(p.anchorId!) : undefined}
                    showCheckbox={false}
                    onToggleSelect={p.anchorId ? () => jumpTo(p.anchorId!) : undefined}
                  />
                ))}
              </ul>
              {unclaimedImages.length > 0 && (
                <div style={{ padding: '4px 12px 10px 12px', flexShrink: 0, borderTop: '1px solid #f0ede9' }}>
                  <div style={{ fontSize: '10px', fontWeight: 600, color: '#aaa', letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: '4px' }}>
                    {t('chat.botMessage.annotatedImage', { defaultValue: 'Analysis Result' })}
                  </div>
                  <div style={{ display: 'flex', gap: '6px', overflowX: 'auto', paddingBottom: '2px' }}>
                    {unclaimedImages.map((url, i) => (
                      <img
                        key={i}
                        src={url}
                        alt={`Annotated analysis ${i + 1}`}
                        onClick={() => setLightboxUrl(url)}
                        style={{
                          height: '52px', width: 'auto', borderRadius: '6px', flexShrink: 0,
                          border: '1px solid #e5e7eb', objectFit: 'cover',
                          cursor: 'pointer',
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {lightboxUrl && (
        <ImageZoomModal src={lightboxUrl} alt="Annotated analysis (zoomed)" onClose={() => setLightboxUrl(null)} />
      )}
    </div>
  );
};

export default PartsTrackerPanel;
